import L from "leaflet";
import { useEffect } from "react";
import { useMap } from "react-leaflet";

/** Confidence-tier marker colors, used by both MarkerDot and Legend. */
export const CONFIDENCE_COLORS = {
  HIGH: "#34d399",      // emerald-400
  MEDIUM: "#fbbf24",    // amber-400
  LOW: "#fb7185",       // rose-400
  UNKNOWN: "#a1a1aa",   // zinc-400
};
export const ALERT_COLOR = "#f43f5e";    // rose-500
export const SELECTED_COLOR = "#a7f3d0"; // emerald-200 ring for selected

/** Google's free directions URL caps multi-stop routes at 9 destinations + origin. */
export const MAX_ROUTE_STOPS = 9;

/** Treat these DCC placeholder strings as "no real name" and fall back to address. */
const NAME_PLACEHOLDER_RE = /^(data not available|tbd\b|n\/a|unknown|none)/i;
export const displayName = (d) => {
  // Prefer the user-set nickname whenever present — it's the storefront
  // / DBA name the team actually uses day-to-day. Fall back to the
  // licensed `name`, and finally to address when the DCC name is a
  // placeholder.
  const nick = (d?.nickname || "").trim();
  if (nick) return nick;
  const n = (d?.name || "").trim();
  if (!n || NAME_PLACEHOLDER_RE.test(n)) {
    return d?.address || d?.city || "Unnamed retailer";
  }
  return n;
};

/** Dark-themed cluster icon matching JackTrack's aesthetic. */
export const clusterIconCreate = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 32 : count < 100 ? 40 : 52;
  return L.divIcon({
    html: `<div class="jacktrack-cluster" style="width:${size}px;height:${size}px;line-height:${size}px;">
      <span>${count}</span>
    </div>`,
    className: "jacktrack-cluster-wrap",
    iconSize: L.point(size, size, true),
  });
};

/** Auto-fits the map's bounds to the given points on mount/update. */
export function FitToMarkers({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 13);
      return;
    }
    const bounds = L.latLngBounds(
      points.map((p) => [p.latitude, p.longitude])
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [points, map]);
  return null;
}

/** Captures the Leaflet map instance into a parent ref for imperative ops. */
export function MapRefCapture({ onMap }) {
  const map = useMap();
  useEffect(() => {
    onMap(map);
  }, [map, onMap]);
  return null;
}
